import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Fields match `views::segmentation` on the backend exactly.
//
// Cells are computed from parsed device config, not canned. A matrix is scoped
// to one region: pass `regionId` to choose, or omit it when the organization
// has exactly one and the backend will use it.

export interface Segment {
  id: string;
  name: string;
  cidr: string | null;
  vlanId: number | null;
  /** What this row/column means, when that needs saying once (the Internet). */
  definition?: string;
}

/** One reason a cell reads the way it does (design §7.1). */
export interface Finding {
  kind: string;
  /** `undecided`: why part of the cell has no verdict. `caveat`: qualifies a verdict that stands. */
  effect: 'undecided' | 'caveat';
  /** One plain sentence. */
  text: string;
  devices: { id: string; name: string }[];
  /** The traffic it is about, in words. */
  traffic?: { src: string; dst: string; protocols: string; ports: string };
}

export interface MatrixCell {
  from: string;
  to: string;
  // "self" | "open" | "filtered" | "blocked" | "unknown"
  status: string;
  services: string[];
  /** When present, `services` amounts to everything except these. */
  except?: string[];
  /** Derived from `explanation`; kept for one release. */
  note: string | null;
  /** Why the cell says what it says. Absent when there is nothing to say. */
  explanation?: Finding[];
  /**
   * Whom the verdict holds for: a set a device on the path fills at runtime,
   * such as a blocklist it enforces outside its config. Only on cells whose
   * traffic crosses that device; absent otherwise.
   */
  notes?: Finding[];
  /**
   * The routes by which traffic gets through or stops being decided, each hop
   * by hop. Blocked routes are left out. Absent when nothing gets through, and
   * on a segment's own diagonal.
   */
  routes?: PathHop[][];
}

/** One device the traffic crosses, named by the interfaces it uses. */
export interface PathHop {
  /** Null for a hop Witina does not manage — named by its address instead. */
  deviceId: string | null;
  device: string;
  /** Absent at the start of a path, where traffic originates. */
  ingress: string | null;
  /** Absent at the end of a path. */
  egress: string | null;
  /** Whether Witina could read a filter config for this hop. */
  inspected: boolean;
}

export interface RegionRef {
  id: string;
  name: string;
}

export interface SegmentationMatrixResponse {
  mock: boolean;
  /**
   * The region this matrix is for. A matrix is always about ONE region,
   * because a region is the unit within which traffic can flow. `null` means
   * there was nothing to draw — the org has no regions, or has several and the
   * request named none.
   */
  regionId: string | null;
  regions: RegionRef[];
  segments: Segment[];
  cells: MatrixCell[];
  computedAt: string;
}

export function useSegmentationMatrix(regionId?: string | null) {
  const contextReady = useActiveContextReady();
  return useQuery<SegmentationMatrixResponse>({
    queryKey: ['segmentationMatrix', regionId ?? null],
    queryFn: async () => {
      const response = await axios.get<SegmentationMatrixResponse>(
        `${config.backendApi}/api/segmentation-matrix`,
        { withCredentials: true, params: regionId ? { region: regionId } : undefined }
      );
      return response.data;
    },
    enabled: contextReady,
  });
}
